import type { AuthUser, LicenseContext } from '@chefiapro/shared';

import { api, setAccessToken, restoreSession } from '@/api/api-client';

export interface Session {
  accessToken: string;
  user: AuthUser;
  license: LicenseContext;
}

export interface LoginCredentials {
  email: string;
  password: string;
  mfaCode?: string;
}

function acceptSession(session: Session): Session {
  setAccessToken(session.accessToken);
  return session;
}

export const authService = {
  async login(credentials: LoginCredentials) {
    return acceptSession((await api.post<Session>('/auth/login', credentials)).data);
  },
  async impersonate(code: string) {
    return acceptSession((await api.post<Session>('/auth/impersonation/exchange', { code })).data);
  },
  async refresh() {
    return restoreSession();
  },
  async logout() {
    try {
      await api.post<void>('/auth/logout');
    } finally {
      setAccessToken(null);
    }
  },
  forgotPassword: async (email: string) =>
    (
      await api.post<{ accepted: boolean; deliveryConfigured: boolean }>('/auth/forgot-password', {
        email,
      })
    ).data,
  resetPassword: async (token: string, password: string, passwordConfirmation: string) =>
    (
      await api.post<{ realm: 'ERP' | 'MASTER' }>('/auth/reset-password', {
        token,
        password,
        passwordConfirmation,
      })
    ).data,
  mfaStatus: async () =>
    (await api.get<{ mfaEnabled: boolean; mfaEnabledAt: string | null }>('/auth/mfa/status')).data,
  mfaSetup: async (currentPassword: string) =>
    (
      await api.post<{ secret: string; qrCodeDataUrl: string; expiresAt: string }>(
        '/auth/mfa/setup',
        {
          currentPassword,
        },
      )
    ).data,
  mfaConfirm: async (code: string) =>
    (await api.post<{ enabled: boolean; recoveryCodes: string[] }>('/auth/mfa/confirm', { code }))
      .data,
  mfaDisable: async (currentPassword: string, code: string) =>
    (await api.post<{ enabled: boolean }>('/auth/mfa/disable', { currentPassword, code })).data,
};
